/*************************************************************************
* Copyright (c) 2025 Omnineura AI Technologies Pvt Limited.
* All rights reserved.
* This code is licensed under the 'Omnineura AI License' found in the LICENSE file.
* Unauthorized copying, sharing, or use of this code is prohibited.
* The intellectual and technical concepts are proprietary to Omnineura AI Technologies Pvt Limited.
* Author: surya@Omnineura.ai
* Date: 11-09-2025
*************************************************************************/



import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { uploadDocumentsApi, checkUploadStatusApi, getDocumentApi, deleteDocumentApi, getDocumentContentApi } from "../services/api";


export const uploadDocuments = createAsyncThunk(
  "documents/uploadDocuments",
  async ({ files }, { dispatch, rejectWithValue }) => {
    try {
      const response = await uploadDocumentsApi(files, (progress) => {
        dispatch(setUploadProgress(progress));
      });
      return response; // { success, task_id, message }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const checkUploadStatus = createAsyncThunk(
  "documents/checkUploadStatus",
  async ({ taskId }, { rejectWithValue }) => {
    try {
      const response = await checkUploadStatusApi(taskId);
      return response; // { status, processed_files, ... }
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

export const fetchDocuments = createAsyncThunk(
  "documents/fetchDocuments",
  async (_, { getState }) => {
    // const token = getState().auth.token
    const res = await getDocumentApi();
    return res.data
  }
)

export const deleteDocument = createAsyncThunk(
  "documents/deleteDocuments",
  async (documentId, { dispatch }) => {
    const res = await deleteDocumentApi(documentId);
    return res;
  })

export const getDocumentContent = createAsyncThunk(
  "documents/getContent",
  async (documentId, { dispatch }) => {

    const uuid = documentId;
    const res = await getDocumentContentApi(uuid);
    return { uuid, ...res }; // attach uuid with content
  }
)



const documentReducer = createSlice({
  name: "documents",
  initialState: {
    loading: false,
    error: null,
    uploading: false,
    uploadProgress: 0, // Real-time upload %
    processingProgress: 0, // Ingestion queue progress %
    documents: [], // documents inside active KB
    status: null, // prevent undefined
    taskId: null,
    selectedDoc: null,
    loadingContent: false,
  },
  reducers: {
    setUploadProgress: (state, action) => {
      state.uploadProgress = action.payload;
    },
    clearUploadState: (state) => {
      state.taskId = null;
      state.status = null;
      state.uploading = false;
      state.uploadProgress = 0;
      state.processingProgress = 0;
    },
    clearSelectedDoc: (state) => {
      state.selectedDoc = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadDocuments.pending, (state) => {
        state.uploading = true;
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.uploadResponse = action.payload;
        state.taskId = action.payload.task_id; // ✅ store taskId
      })
      .addCase(uploadDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(checkUploadStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.uploading = false;
        state.status = action.payload.result;
        state.processingProgress = action.payload.progress || 0;
      })
      .addCase(fetchDocuments.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.documents = action.payload.documents;
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteDocument.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.loading = false;
        // remove the document from state if you keep docs in store
        const deletedId = action.payload?.data?.data?.document_uuid;
        state.documents = state.documents.filter(
          (doc) => doc.uuid !== deletedId
        );
      })
      .addCase(deleteDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getDocumentContent.pending, (state) => {
        state.loadingContent = true;
        state.error = null;
      })
      .addCase(getDocumentContent.fulfilled, (state, action) => {
        state.loadingContent = false;
        const uuid = action.payload.uuid;
        const content = action.payload.data?.content;
        state.selectedDoc = {
          uuid,
          content,
        };
      })
      .addCase(getDocumentContent.rejected, (state, action) => {
        state.loadingContent = false;
        const payload = action.payload;
        let message = "Failed to fetch content";

        if (typeof payload === "string") {
          message = payload;
        } else if (payload) {
          // Support typical FastAPI error shapes or our own error objects
          message = payload.detail || payload.error || message;
        }

        state.error = message;
      });
  },
});

export const { setUploadProgress, clearUploadState, clearSelectedDoc } = documentReducer.actions;
export default documentReducer.reducer;

